import React from 'react';

import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { AnchorWallets } from '@/lib/api/auth';
import { cn } from '@/lib/utils';

import {
    ANCHOR_CHAIN_IDS,
    ANCHOR_CHAINS,
    type AnchorChainId,
} from '../config/anchors';

interface AnchorChainToggleProps {
    value: AnchorChainId;
    onChange: (chainId: AnchorChainId) => void;
    /**
     * `/v2/auth/me.anchors`: which anchors this account already has a wallet on.
     * A hint for the label only, so a user with an existing wallet can see where
     * it is rather than guessing. Absent (older backend, or not signed in) simply
     * shows no hint.
     */
    anchors?: AnchorWallets | null;
    disabled?: boolean;
    className?: string;
    /** Short line under the toggle; pass null to leave it out. */
    description?: string | null;
}

/**
 * Which chain a Snappy identity is anchored on. Two options, both real: a user
 * may hold an authorization wallet on either chain, or on both, and the choice
 * decides which wallet a trading window (or a new account) belongs to.
 */
const AnchorChainToggle: React.FC<AnchorChainToggleProps> = ({
    value,
    onChange,
    anchors = null,
    disabled = false,
    className,
    description = 'This is the network your Snappy account lives on.',
}) => (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
        <ToggleGroup
            type="single"
            value={String(value)}
            aria-label="Account network"
            onValueChange={(next) => {
                const parsed = Number(next);
                if (
                    (ANCHOR_CHAIN_IDS as readonly number[]).includes(parsed) &&
                    parsed !== value
                ) {
                    onChange(parsed as AnchorChainId);
                }
            }}
            className="w-full"
        >
            {ANCHOR_CHAIN_IDS.map((chainId) => {
                const existing = anchors?.[String(chainId) as '25' | '4663'];
                return (
                    <ToggleGroupItem
                        key={chainId}
                        value={String(chainId)}
                        disabled={disabled}
                        className="flex-1"
                    >
                        {ANCHOR_CHAINS[chainId].label}
                        {existing ? ' (account here)' : ''}
                    </ToggleGroupItem>
                );
            })}
        </ToggleGroup>
        {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
        )}
    </div>
);

AnchorChainToggle.displayName = 'AnchorChainToggle';

export { AnchorChainToggle };
